import PTPEquipmentPanel from '../PTPEquipmentPanel';
import {
  PTPChoices,
  PTPFormPanel,
  PTPPanelConfigImpl,
  syncFormState,
  getEndFromChoices,
  getEquipmentFromChoices,
  valuesFromEqChoices,
  isChoicesComplex,
} from '../utils';
import {
  genericEndPerformanceSummary,
  genericLinkSummary,
  genericEndConfig,
  remoteProductNextValue,
} from './config-utils';
import additionalMessages from 'src/messages';
import { OperatingConditions } from 'src/components/OperatingConditions';

const equipment: PTPFormPanel<any> = {
  kind: 'equipment',
  title: 'Equipment',
  name: 'equipment_link_a',
  component: PTPEquipmentPanel,
  fields: [
    {
      attrName: 'band',
      getter: 'local.radios.0.equipment.band',
      lk: {
        '2plus0': {
          getter: [
            'local.radios.0.equipment.band',
            'local.radios.1.equipment.band',
          ],
        },
      },
      nextValueExtra(newChoices, getValues, setValue) {
        // When the product changes the Link B band
        // may be invalid for PTP 550 / 2+0 links.
        // Reset the band if the current option is not
        // in the choices.
        if (isChoicesComplex(newChoices)) {
          const currentBandLinkB = getValues('local.radios.1.equipment.band');
          const [newBandLinkB] = valuesFromEqChoices(
            getEquipmentFromChoices(newChoices, 1),
            ['band']
          );
          if (currentBandLinkB !== newBandLinkB) {
            setValue(`local.radios.1.equipment.band`, newBandLinkB, {
              shouldDirty: true,
            });
          }
        }
      },
    },
    {
      attrName: 'product',
      getter: 'local.radios.0.equipment.product',
    },
    {
      attrName: 'remote_product',
      getter: 'remote.radios.0.equipment.product',
      nextValue: remoteProductNextValue,
    },
    {
      attrName: 'regulation',
      getter: 'local.radios.0.equipment.regulation',
    },
    {
      attrName: 'link_protection',
      getter: 'local.radios.0.equipment.link_protection',
      afterOnChange(newValue, { getValues, setValue }) {
        // reset frequencies whenever link kind changes
        const radios = getValues('local.radios');
        for (let i = 0; i < radios.length; i++) {
          // hack: copy across paths to prevent frequency endpoint failing
          // (changing from 4+0 to 1+1 didn't work - not sure why it works
          // for other link kinds)
          setValue(`local.radios.${i}.equipment.link_protection`, newValue);
          setValue(`remote.radios.${i}.equipment.link_protection`, newValue);
          // reset freqs
          setValue(`local.radios.${i}.frequency.tx_frequency`, null, {
            shouldDirty: true,
          });
          setValue(`remote.radios.${i}.frequency.tx_frequency`, null, {
            shouldDirty: true,
          });
        }
      },
    },
    {
      attrName: 'ptp_mode',
      getter: 'local.radios.0.equipment.ptp_mode',
    },
  ],
};

const config: PTPFormPanel<any> = {
  kind: 'config',
  title: 'Product Configuration',
  name: 'equipment_config_link_a',
  component: PTPEquipmentPanel,
  fields: [
    {
      attrName: 'bandwidth',
      getter: 'local.radios.0.equipment.bandwidth',
      lk: {
        '2plus0': {
          getter: [
            'local.radios.0.equipment.bandwidth',
            'local.radios.1.equipment.bandwidth',
          ],
        },
      },
    },
    {
      attrName: 'dl_ul_ratio',
      getter: 'local.radios.0.equipment.dl_ul_ratio',
    },
    {
      attrName: 'frame_period',
      getter: 'local.radios.0.equipment.frame_period',
      units: 'ms',
    },
    {
      attrName: 'max_mod_mode',
      getter: 'local.radios.0.equipment.max_mod_mode',
      lk: {
        '2plus0': {
          getter: [
            'local.radios.0.equipment.max_mod_mode',
            'local.radios.1.equipment.max_mod_mode',
          ],
        },
      },
    },
    {
      attrName: 'guard_interval',
      getter: 'local.radios.0.equipment.guard_interval',
    },
    {
      attrName: 'master',
      getter: 'local.radios.0.equipment.master',
    },
    {
      attrName: 'gb_ethernet_port',
      getter: 'local.radios.0.equipment.gb_ethernet_port',
    },
  ],
};

function getSummaryOperatingConditions(): PTPFormPanel<any> {
  return {
    name: 'summary_operating_conditions',
    title: additionalMessages.operatingConditions,
    show: ({ choices }: { choices: PTPChoices }) => {
      return getEndFromChoices(choices, 'local')?.supports_frame_size ?? false;
    },
    component: OperatingConditions,
    fields: [
      {
        label: 'Frame Size',
        getter: 'summary.0.link.frame_size',
        choicesGetter: 'local.end.frame_size_choices',
        valueGetter: 'local.end.default_frame_size',
        units: 'Bytes',
        lk: {
          choicesGetter: 'paths.0.local.end.frame_size_choices',
          valueGetter: 'paths.0.local.end.default_frame_size',
        },
        nextValue(currentValue: string, newChoices: PTPChoices) {
          const endChoices = getEndFromChoices(newChoices, 'local');
          const choices = endChoices.frame_size_choices;
          if (choices != null) {
            if (!choices.includes(currentValue)) {
              return endChoices.default_frame_size;
            }
          }
          return null;
        },
      },
    ],
  };
}

const PTPePMPPanelConfig: PTPPanelConfigImpl = {
  syncFormState,
  equipment: [equipment, config],
  performance: {
    summary: {
      link: [getSummaryOperatingConditions(), genericLinkSummary()],
      local: genericEndPerformanceSummary('local'),
      remote: genericEndPerformanceSummary('remote'),
    },
  },
  ends: {
    title: 'Configuration at Each End',
    local: genericEndConfig('local'),
    remote: genericEndConfig('remote'),
  },
};

export default PTPePMPPanelConfig;
